import { lazy, type ComponentType, type LazyExoticComponent } from "react";
import { Sentry } from "@/lib/sentry";

const RELOAD_MARKER_KEY = "candyman:chunk-reload-at";
const RELOAD_GUARD_MS = 30_000;

const CHUNK_ERROR_PATTERNS = [
  /Loading chunk \d+ failed/i,
  /Loading CSS chunk \d+ failed/i,
  /ChunkLoadError/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /Unable to preload CSS/i,
];

export function isChunkLoadError(err: unknown): boolean {
  // Only classify as chunk-load when there is explicit evidence: a
  // matching error name, matching message text, or a matching string.
  // null/undefined and unrelated thrown values fall through to the
  // normal error path so genuine bugs stay loud.
  if (err == null) return false;
  if (typeof err === "string") {
    return CHUNK_ERROR_PATTERNS.some((re) => re.test(err));
  }
  if (err instanceof Error) {
    if (err.name === "ChunkLoadError") return true;
    const msg = err.message ?? "";
    return CHUNK_ERROR_PATTERNS.some((re) => re.test(msg));
  }
  try {
    const msg = String((err as { message?: unknown })?.message ?? err);
    return CHUNK_ERROR_PATTERNS.some((re) => re.test(msg));
  } catch {
    return false;
  }
}

function readMarker(): number | null {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_MARKER_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeMarker(ts: number): void {
  try {
    window.sessionStorage.setItem(RELOAD_MARKER_KEY, String(ts));
  } catch {
    /* ignore quota/disabled storage */
  }
}

function clearMarker(): void {
  try {
    window.sessionStorage.removeItem(RELOAD_MARKER_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Returns true if we just reloaded recently due to a chunk-load failure.
 * After RELOAD_GUARD_MS the marker is treated as stale and cleared so a
 * future stale-deploy event can recover again.
 */
function recentlyReloaded(): boolean {
  const ts = readMarker();
  if (ts == null) return false;
  if (Date.now() - ts > RELOAD_GUARD_MS) {
    clearMarker();
    return false;
  }
  return true;
}

/**
 * Core recovery primitive: if we haven't already reloaded in the last
 * RELOAD_GUARD_MS, set a marker and reload. Caller is responsible for
 * deciding whether the error qualifies. Returns true if a reload was
 * initiated.
 */
function attemptRecoveryReload(err: unknown): boolean {
  if (typeof window === "undefined") return false;
  if (recentlyReloaded()) return false;
  writeMarker(Date.now());
  // Report as a distinct, low-severity event so we still have visibility
  // without polluting the error feed.
  try {
    let message: string;
    if (err instanceof Error) message = err.message;
    else if (err == null) message = "(empty rejection)";
    else {
      try {
        message = String((err as { message?: unknown })?.message ?? err);
      } catch {
        message = "(unserialisable rejection)";
      }
    }
    Sentry.captureMessage("chunk-load auto-recovery: reloading", {
      level: "info",
      tags: { kind: "chunk-load-recovery" },
      extra: { message, href: window.location.href },
    });
  } catch {
    /* swallow — Sentry is best-effort */
  }
  window.location.reload();
  return true;
}

/**
 * If `err` looks like a stale-deploy chunk-load failure and we haven't
 * already reloaded in the last RELOAD_GUARD_MS, set a marker and reload.
 * Returns true if a reload was initiated (caller should stop rendering).
 * Intended for the ErrorBoundary path, where the error has bubbled up
 * through React and we need explicit evidence it's a chunk-load before
 * acting (so we don't reload genuine render bugs).
 */
export function tryRecoverFromChunkLoadError(err: unknown): boolean {
  if (!isChunkLoadError(err)) return false;
  return attemptRecoveryReload(err);
}

/**
 * Drop the reload marker once the app has successfully mounted after a
 * recovery reload, so a future stale-deploy event can recover again.
 */
export function clearChunkReloadMarker(): void {
  clearMarker();
}

// Match React.lazy's signature (ComponentType<any>) so importers for
// components with required props type-check without losing their prop
// shape at the call site.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches React.lazy's ComponentType<any> generic constraint
type Importer<T extends ComponentType<any>> = () => Promise<{ default: T }>;

/**
 * Wrap `React.lazy()` so a rejected dynamic import triggers a one-shot
 * auto-reload (stale deploy recovery) instead of bubbling to the error
 * boundary. If recovery is unavailable (e.g. we've already reloaded
 * once), the rejection is re-thrown so the ErrorBoundary can render its
 * "Updating…" UI.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches React.lazy's ComponentType<any> generic constraint
export function lazyWithChunkRecovery<T extends ComponentType<any>>(
  importer: Importer<T>,
): LazyExoticComponent<T> {
  return lazy(() =>
    importer().catch((err) => {
      // We intentionally bypass `isChunkLoadError` here: any rejection
      // bubbling out of a dynamic import is, by construction, a
      // module-load failure — including opaque `{}` rejections that
      // some browsers (notably Safari) throw for a stale-deploy 404,
      // which is exactly the shape that produced the recent
      // `ErrorBoundary caught: {}` report. The 30s guard window in
      // `attemptRecoveryReload` prevents a persistent failure from
      // looping.
      if (attemptRecoveryReload(err)) {
        // Reload is in flight — return a never-resolving promise so
        // Suspense keeps showing the fallback until the page navigates.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }),
  );
}
